import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  SelectChangeEvent,
  Chip,
  IconButton,
  Collapse,
  Typography,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import { permissionService } from '../services/permissionService';

interface ControllerWidgetProps {
  selectedMenu: string | null;
  tableColumns: string[];
  visibleColumns?: string[];
  onSearch: (column: string, searchValue: string) => void;
  onVisibleColumnsChange: (visibleColumns: string[]) => void;
  onAddRecord?: () => void;
  onGetSuggestions?: (searchValue: string) => Promise<string[]>;
  createPermission?: string;
}

const ControllerWidget: React.FC<ControllerWidgetProps> = ({ 
  selectedMenu, 
  tableColumns, 
  visibleColumns,
  onSearch,
  onVisibleColumnsChange,
  onAddRecord,
  onGetSuggestions,
  createPermission
}) => {
  const canCreate = createPermission ? permissionService.hasPermission(createPermission) : true;
  const [selectedColumn, setSelectedColumn] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [showColumnSelector, setShowColumnSelector] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState<boolean>(false);
  const displayableColumns = tableColumns.filter((column) => column !== 'id');
  const currentVisibleColumns = visibleColumns ?? displayableColumns;

  const formatColumnLabel = (key: string): string => {
    const translations: { [key: string]: string } = {
      ajanlatiSzam: 'Ajánlati szám',
      ajanlatTargya: 'Ajánlat tárgya',
      valaszDatum: 'Válasz dátum',
      iktatasIranya: 'Iktatás iránya',
      munkaTargya: 'Munka tárgya',
      iktatasTipusa: 'Iktatás típusa',
      iratTargya: 'Irat tárgya',
      cimzettKuldo: 'Címzett',
      iratHelye: 'Irat helye',
      cegnev: 'Cégnév',
      iranyitoszam: 'Irányítószám',
      megrendeloNeve: 'Megrendelő neve',
      ev: 'Év',
      sorszam: 'Sorszám',
      datum: 'Dátum',
      torzsszam: 'Törzsszám',
      megrendelo: 'Megrendelő',
      ugyintezo: 'Ügyintéző',
      valasz: 'Válasz',
      irsz: 'Irsz.',
      cim: 'Cím',
      cegjegyzekszam: 'Cégjegyzékszám',
      adoszam: 'Adószám',
      bankszamlaszam: 'Bankszámlaszám',
      kepviselo: 'Képviselő',
      munkaszam: 'Munkaszám',
      resztvevok: 'Résztvevők',
      projektKezdete: 'Projekt kezdete',
      tervezettBefejezes: 'Tervezett befejezés',
      iktatoszam: 'Iktatószám',
      utolsoIdobejegjzes: 'Utolsó időbejegyzés',
      osszesIdo: 'Összes idő (felh. idő)',
      osszesOraszam: 'Összes óra',
      utolsoIdoraforditasDatum: 'Utolsó dátum',
      utolsoComment: 'Utolsó megjegyzés',
      bejegyzesTargya: 'Bejegyzés tárgya',
      idoraforditasErteke: 'Időráfordítás (óra)',
      munkaszamErteke: 'Munkaszám',
      felhasznaloNev: 'Felhasználó',
      workLocation: 'Helyszín',
      workType: 'Munka típusa',
      id: 'ID',
    };

    return translations[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  // Reset when menu changes
  useEffect(() => {
    setSelectedColumn('');
    setSearchValue('');
    setShowColumnSelector(false);
    setSuggestions([]);
  }, [selectedMenu]);

  // Set default column when columns change and initialize visible columns
  useEffect(() => {
    if (displayableColumns.length > 0) {
      if (visibleColumns === undefined) {
        onVisibleColumnsChange(displayableColumns);
        return;
      }

      // Meglévő választás megtartása, csak a már nem létező oszlopokat dobjuk
      const normalized = visibleColumns.filter((column) => displayableColumns.includes(column));

      // Ha menü/adat váltás után kiürülne a lista, álljunk vissza az összes megjeleníthető oszlopra.
      // Az "Egyik sem" gomb tudatos [] állapotát ne írjuk felül.
      if (visibleColumns.length > 0 && normalized.length === 0 && displayableColumns.length > 0) {
        onVisibleColumnsChange(displayableColumns);
        return;
      }

      if (normalized.length !== visibleColumns.length) {
        onVisibleColumnsChange(normalized);
      }
    }
  }, [displayableColumns, visibleColumns, onVisibleColumnsChange]);

  // Fetch suggestions when search value changes (min 3 characters)
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchValue.length >= 3 && onGetSuggestions) {
        setIsLoadingSuggestions(true);
        try {
          const results = await onGetSuggestions(searchValue);
          setSuggestions(results);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
        } finally {
          setIsLoadingSuggestions(false);
        }
      } else {
        setSuggestions([]);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchValue, onGetSuggestions]);

  const handleColumnChange = (event: SelectChangeEvent) => {
    setSelectedColumn(event.target.value);
  };

  const handleSearch = () => {
    if (searchValue.trim()) {
      // If no column selected, use empty string to indicate "all columns"
      const columnToSearch = selectedColumn || '';
      onSearch(columnToSearch, searchValue.trim());
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClear = () => {
    setSearchValue('');
    setSuggestions([]);
    // Reset search with empty column to show all results
    onSearch('', '');
  };

  const toggleColumnVisibility = (column: string) => {
    const baseColumns = currentVisibleColumns;
    const newVisibleColumns = baseColumns.includes(column)
      ? baseColumns.filter(col => col !== column)
      : [...baseColumns, column];
    
    onVisibleColumnsChange(newVisibleColumns);
  };

  const toggleColumnSelector = () => {
    setShowColumnSelector(!showColumnSelector);
  };

  const showAllColumns = () => {
    onVisibleColumnsChange(displayableColumns);
  };

  const hideAllColumns = () => {
    onVisibleColumnsChange([]);
  };

  // Don't render if no menu is selected or no columns available
  if (!selectedMenu || displayableColumns.length === 0) {
    return null;
  }

  return (
    <Card sx={{ 
      backgroundColor: 'white', 
      boxShadow: 1, 
      minHeight: showColumnSelector ? '180px' : '80px', // Use minHeight instead of fixed height
      transition: 'min-height 0.3s ease-in-out'
    }}>
      <CardContent sx={{ 
        py: 2, 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: showColumnSelector ? 'flex-start' : 'center',
        minHeight: showColumnSelector ? '128px' : '48px' // Account for padding (160-32=128, 80-32=48)
      }}>
        {/* First Row - Search Controls */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          flexWrap: 'wrap',
          justifyContent: 'space-between' 
        }}>
          {/* Left side - Search Controls Group */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* Column Selector */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Oszlop (üres = összes)</InputLabel>
              <Select
                value={selectedColumn}
                label="Oszlop (üres = összes)"
                onChange={handleColumnChange}
              >
              <MenuItem value="">
                <em>Összes oszlop</em>
              </MenuItem>
              {displayableColumns.map((column) => (
                <MenuItem key={column} value={column}>
                  {formatColumnLabel(column)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Search Input with Autocomplete */}
          <Autocomplete
            freeSolo
            options={suggestions}
            loading={isLoadingSuggestions}
            inputValue={searchValue}
            onInputChange={(event, newValue) => {
              setSearchValue(newValue);
            }}
            sx={{ minWidth: 250, width: 350 }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Keresési érték"
                placeholder="Min. 3 karakter..."
                onKeyPress={handleKeyPress}
              />
            )}
            noOptionsText={
              searchValue.length < 3 
                ? "Írj be legalább 3 karaktert" 
                : "Nincs találat"
            }
          />

          {/* Search Button */}
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={!searchValue.trim()}
            sx={{ 
              height: '40px',
              minWidth: '100px'
            }}
          >
            Keresés
          </Button>

          {/* Clear Button */}
          {searchValue && (
            <Button
              variant="outlined"
              onClick={handleClear}
              sx={{ 
                height: '40px',
                minWidth: '80px'
              }}
            >
              Törlés
            </Button>
          )}
          </Box>

          {/* Right side - Action Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Add Button - csak akkor jelenik meg, ha onAddRecord meg van adva ÉS van jogosultság */}
            {onAddRecord && canCreate && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAddRecord}
                sx={{ 
                  height: '40px',
                  minWidth: '110px'
                }}
              >
                Hozzáadás
              </Button>
            )}

            {/* Column Visibility Toggle */}
            <IconButton
              onClick={toggleColumnSelector}
              sx={{ 
                color: showColumnSelector ? 'primary.main' : 'text.secondary' 
              }}
              title="Oszlop láthatóság beállítása"
            >
              <VisibilityIcon />
              {showColumnSelector ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Second Row - Column Visibility Controls */}
        <Collapse 
          in={showColumnSelector}
          timeout={{ enter: 300, exit: 300 }}
        >
          <Box sx={{ 
            mt: 2, 
            pt: 2, 
            borderTop: '1px solid #e0e0e0'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Oszlopok láthatósága:
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={showAllColumns}
                sx={{ minWidth: 'auto', p: 0.5 }}
              >
                Mind
              </Button>
              <Typography variant="body2" color="text.disabled">|</Typography>
              <Button
                size="small"
                variant="text"
                onClick={hideAllColumns}
                sx={{ minWidth: 'auto', p: 0.5 }}
              >
                Egyik sem
              </Button>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              maxHeight: '80px', // More space for chips
              overflow: 'auto', // Add scroll if needed
              pr: 1 // Add some padding for scrollbar
            }}>
              {displayableColumns.map((column) => (
                <Chip
                  key={column}
                  label={formatColumnLabel(column)}
                  onClick={() => toggleColumnVisibility(column)}
                  color={currentVisibleColumns.includes(column) ? 'primary' : 'default'}
                  variant={currentVisibleColumns.includes(column) ? 'filled' : 'outlined'}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { 
                      backgroundColor: currentVisibleColumns.includes(column) 
                        ? 'primary.dark' 
                        : 'action.hover' 
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ControllerWidget;
